import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { Bot, Send, Sparkles, Trash2, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  "How do I reset my work password?",
  "My VPN keeps disconnecting",
  "Outlook is not syncing emails",
  "How do I set up 2FA on my account?",
];

function formatContent(text) {
  // Simple markdown-ish: bold + lines
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return p.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>
        {line}
        {j < arr.length - 1 && <br />}
      </span>
    ));
  });
}

function downscaleImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (file.size < 1024 * 1024) {
        resolve({ dataUrl: reader.result, mime: file.type });
        return;
      }
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1400 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.85), mime: "image/jpeg" });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/chat/history").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamedText]);

  const attachFile = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg"].includes(f.type)) {
      toast.error("Only JPG and PNG screenshots are supported");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Screenshot must be under 8 MB");
      return;
    }
    const { dataUrl, mime } = await downscaleImage(f);
    setAttachment({ dataUrl, mime, name: f.name });
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    const img = attachment;
    if ((!message && !img) || streaming) return;
    setInput("");
    setAttachment(null);
    const shownText = message || "Please look at this screenshot and help me fix the issue.";
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: shownText, image: img?.dataUrl, created_at: new Date().toISOString() }]);
    setStreaming(true);
    setStreamedText("");

    try {
      const token = localStorage.getItem("hdp_token");
      const body = { message };
      if (img) {
        body.image_base64 = img.dataUrl.split(",")[1];
        body.mime_type = img.mime;
      }
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok || !res.body) {
        toast.error("Chat failed. Please try again.");
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (chunk.startsWith("data: ")) {
            const data = chunk.slice(6);
            if (data === "[DONE]") continue;
            let piece;
            try { piece = JSON.parse(data); } catch { piece = data; }
            if (typeof piece === "string" && piece.startsWith("[error]")) {
              toast.error("AI error. Please try again.");
              continue;
            }
            acc += piece;
            setStreamedText(acc);
          }
        }
      }

      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}`, role: "assistant", content: acc, created_at: new Date().toISOString() },
      ]);
      setStreamedText("");
    } catch (e) {
      toast.error("Network error. Please try again.");
    } finally {
      setStreaming(false);
    }
  };

  const clearHistory = async () => {
    if (!confirm("Clear chat history?")) return;
    await api.delete("/chat/history");
    setMessages([]);
    toast.success("Chat cleared");
  };

  return (
    <div className="flex flex-col h-screen" data-testid="chat-page">
      {/* Header */}
      <div className="px-8 py-5 border-b border-zinc-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-lg text-zinc-900">AI Helpdesk Assistant</div>
            <div className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> Online · GPT-5.2
            </div>
          </div>
        </div>
        <button
          onClick={clearHistory}
          className="text-zinc-500 hover:text-red-600 text-sm font-semibold flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-zinc-100 transition"
          data-testid="chat-clear"
        >
          <Trash2 className="w-4 h-4" /> Clear
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-8 py-6 bg-zinc-50" data-testid="chat-messages">
        <div className="max-w-3xl mx-auto space-y-5">
          {messages.length === 0 && !streaming && (
            <div className="text-center py-12 fade-up" data-testid="chat-empty">
              <div className="w-16 h-16 bg-zinc-900 rounded-2xl mx-auto flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-blue-400" />
              </div>
              <h2 className="font-display font-black tracking-tighter text-3xl text-zinc-900 mb-2">How can I help?</h2>
              <p className="text-zinc-500 mb-8">Try one of these — or just type your problem below.</p>
              <div className="grid sm:grid-cols-2 gap-2 max-w-xl mx-auto">
                {QUICK_PROMPTS.map((p, i) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="text-left p-4 bg-white border border-zinc-200 rounded-2xl text-sm font-medium text-zinc-700 hover:border-zinc-900 hover:shadow-md transition-all"
                    data-testid={`quick-prompt-${i}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m) =>
            m.role === "user" ? (
              <div key={m.id} className="flex justify-end" data-testid="msg-user">
                <div className="bg-zinc-900 text-white text-sm px-5 py-3 rounded-2xl rounded-br-sm max-w-[80%] whitespace-pre-wrap">
                  {(m.image || m.image_base64) && (
                    <img
                      src={m.image || `data:${m.mime_type || "image/jpeg"};base64,${m.image_base64}`}
                      alt="Uploaded screenshot"
                      className="rounded-xl mb-2 max-h-48 border border-white/20"
                      data-testid="msg-user-image"
                    />
                  )}
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={m.id} className="flex gap-3" data-testid="msg-assistant">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white border border-zinc-200 px-5 py-4 rounded-2xl rounded-bl-sm max-w-[80%] text-sm text-zinc-800 leading-relaxed">
                  {formatContent(m.content)}
                </div>
              </div>
            )
          )}

          {streaming && (
            <div className="flex gap-3" data-testid="msg-streaming">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white border border-zinc-200 px-5 py-4 rounded-2xl rounded-bl-sm max-w-[80%] text-sm text-zinc-800 leading-relaxed">
                {streamedText ? (
                  <span>
                    {formatContent(streamedText)}
                    <span className="caret" />
                  </span>
                ) : (
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full dot-1" />
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full dot-2" />
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full dot-3" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="px-8 py-5 border-t border-zinc-200 bg-white">
        {attachment && (
          <div className="max-w-3xl mx-auto mb-3 flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-2xl p-3" data-testid="chat-attachment-chip">
            <img src={attachment.dataUrl} alt="Screenshot to send" className="h-14 rounded-lg border border-zinc-200" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zinc-900 truncate">{attachment.name}</div>
              <div className="text-xs text-zinc-500">Screenshot attached — the AI will analyze it</div>
            </div>
            <button
              onClick={() => setAttachment(null)}
              className="p-2 rounded-full hover:bg-zinc-200 text-zinc-500 transition"
              data-testid="chat-attachment-remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="max-w-3xl mx-auto flex gap-3"
          data-testid="chat-form"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => attachFile(e.target.files?.[0])}
            data-testid="chat-file-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={streaming}
            title="Upload a screenshot of the issue"
            className="border-2 border-zinc-200 text-zinc-600 rounded-full p-3.5 hover:border-blue-600 hover:text-blue-600 disabled:opacity-50 transition-all"
            data-testid="chat-attach-btn"
          >
            <ImagePlus className="w-5 h-5" />
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={streaming}
            placeholder={attachment ? "Describe the issue (optional) — or just hit send" : "Ask anything — or attach a screenshot of the error"}
            className="flex-1 rounded-full border-2 border-zinc-200 px-6 py-3.5 text-sm focus:border-blue-600 focus:outline-none transition disabled:opacity-50"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={streaming || (!input.trim() && !attachment)}
            className="bg-zinc-900 text-white rounded-full p-3.5 hover:bg-zinc-800 disabled:opacity-50 transition-all hover:-translate-y-0.5"
            data-testid="chat-send"
          >
            {streaming ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </form>
        <div className="max-w-3xl mx-auto mt-2 text-[10px] text-zinc-400 text-center uppercase tracking-widest font-bold">
          AI may be inaccurate. Verify critical instructions.
        </div>
      </div>
    </div>
  );
}
